import {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {
  createCode,
  consumeCode,
  resendCode,
  clearLoginAttemptInfo,
} from "supertokens-auth-react/recipe/passwordless";
import {toast} from "sonner";
import {ArrowLeft, Mail} from "lucide-react";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Spinner} from "@/components/ui/spinner";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot} from "@/components/ui/input-otp";
import {AUTH_ERROR_CODES, getAuthErrorMessage} from "@/lib/auth-errors";
import * as m from "@/paraglide/messages";

type PasswordlessStep = "email" | "otp";

type EmailFormValues = {
  email: string;
};

interface PasswordlessFormProps {
  onVerified: () => void;
  onError: (message: string) => void;
  disabled?: boolean;
}

const RESEND_COOLDOWN_SECONDS = 30;
const OTP_LENGTH = 6;

// Schema created inside component so messages are evaluated with current locale
const emailSchema = z.object({
  email: z.email({message: m.form_email_invalid()}).nonempty({message: m.form_email_required()}),
});

export function PasswordlessForm({onVerified, onError, disabled}: PasswordlessFormProps) {
  const [step, setStep] = useState<PasswordlessStep>("email");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attemptsInfo, setAttemptsInfo] = useState<{
    remaining: number;
    max: number;
  } | null>(null);

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-submit when OTP is complete
  useEffect(() => {
    if (otp.length === OTP_LENGTH && !isSubmitting) {
      handleVerifyOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleEmailSubmit = async (data: EmailFormValues) => {
    setIsSubmitting(true);
    try {
      const response = await createCode({email: data.email});
      if (response.status === "OK") {
        setEmail(data.email);
        setStep("otp");
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        toast.success(m.auth_magic_link_sent({email: data.email}));
      } else if (response.status === "SIGN_IN_UP_NOT_ALLOWED") {
        const errorMsg = getAuthErrorMessage(AUTH_ERROR_CODES.NOT_ALLOWED);
        toast.error(errorMsg);
        onError(errorMsg);
      } else {
        const errorMsg = getAuthErrorMessage(AUTH_ERROR_CODES.SEND_FAILED);
        toast.error(errorMsg);
        onError(errorMsg);
      }
    } catch (err) {
      console.error("Create code error:", err);
      const errorMsg = getAuthErrorMessage(AUTH_ERROR_CODES.GENERAL);
      toast.error(errorMsg);
      onError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== OTP_LENGTH) return;

    setIsSubmitting(true);
    try {
      const response = await consumeCode({
        userInputCode: otp,
      });

      if (response.status === "OK") {
        onVerified();
      } else if (response.status === "INCORRECT_USER_INPUT_CODE_ERROR") {
        const remaining = response.maximumCodeInputAttempts - response.failedCodeInputAttemptCount;
        setAttemptsInfo({remaining, max: response.maximumCodeInputAttempts});
        setOtp("");
      } else if (response.status === "EXPIRED_USER_INPUT_CODE_ERROR") {
        const errorMsg = getAuthErrorMessage(AUTH_ERROR_CODES.OTP_EXPIRED);
        toast.error(errorMsg);
        setOtp("");
        setAttemptsInfo(null);
      } else if (response.status === "RESTART_FLOW_ERROR") {
        toast.error(m.auth_error_otp_expired());
        handleReset();
      } else if (response.status === "SIGN_IN_UP_NOT_ALLOWED") {
        const errorMsg = getAuthErrorMessage(AUTH_ERROR_CODES.NOT_ALLOWED);
        toast.error(errorMsg);
        onError(errorMsg);
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      const errorMsg = getAuthErrorMessage(AUTH_ERROR_CODES.GENERAL);
      toast.error(errorMsg);
      onError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setIsSubmitting(true);
    try {
      const response = await resendCode();
      if (response.status === "OK") {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setOtp("");
        setAttemptsInfo(null);
        toast.success(m.auth_code_resent());
      } else if (response.status === "RESTART_FLOW_ERROR") {
        toast.error(m.auth_error_otp_expired());
        handleReset();
      }
    } catch (err) {
      console.error("Resend code error:", err);
      const errorMsg = getAuthErrorMessage(AUTH_ERROR_CODES.SEND_FAILED);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep("email");
    setEmail("");
    setOtp("");
    setResendCooldown(0);
    setAttemptsInfo(null);
    clearLoginAttemptInfo();
    form.reset();
  };

  const isDisabled = disabled || isSubmitting;

  // Step 1: Email Entry
  if (step === "email") {
    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleEmailSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({field}) => (
              <FormItem>
                <FormLabel>{m.form_email_label()}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={m.form_email_placeholder()}
                    autoComplete="email"
                    disabled={isDisabled}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="h-12 w-full text-base" disabled={isDisabled}>
            {isSubmitting ? (
              <>
                <Spinner className="mr-2 h-5 w-5" />
                {m.form_sending()}
              </>
            ) : (
              <>
                <Mail className="mr-2 h-5 w-5" />
                {m.form_send_code()}
              </>
            )}
          </Button>
        </form>
      </Form>
    );
  }

  // Step 2: OTP Entry
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="bg-primary/10 text-primary mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
          <Mail className="h-8 w-8" />
        </div>
        <h3 className="text-lg font-semibold">{m.auth_otp_title()}</h3>
        <p className="text-muted-foreground mt-1 text-sm">{m.auth_otp_description({email})}</p>
        <p className="text-muted-foreground mt-1 text-sm">{m.auth_otp_or_magic_link()}</p>
      </div>

      {/* OTP Input */}
      <div className="flex flex-col items-center space-y-4">
        <InputOTP
          maxLength={OTP_LENGTH}
          value={otp}
          onChange={setOtp}
          disabled={isDisabled}
          autoFocus
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        {/* Attempts remaining indicator */}
        {attemptsInfo && attemptsInfo.remaining < attemptsInfo.max && (
          <p className="text-destructive text-sm">
            {m.auth_error_otp_incorrect({remaining: attemptsInfo.remaining.toString()})}
          </p>
        )}
      </div>

      {/* Verify Button */}
      <Button
        onClick={handleVerifyOtp}
        className="h-12 w-full text-base"
        disabled={isDisabled || otp.length !== OTP_LENGTH}
      >
        {isSubmitting ? (
          <>
            <Spinner className="mr-2 h-5 w-5" />
            {m.auth_otp_verifying()}
          </>
        ) : (
          m.form_submit_code()
        )}
      </Button>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={handleReset} disabled={isDisabled}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          {m.auth_use_different_email()}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleResend}
          disabled={isDisabled || resendCooldown > 0}
        >
          {resendCooldown > 0
            ? m.auth_otp_resend_countdown({seconds: resendCooldown.toString()})
            : m.auth_otp_resend()}
        </Button>
      </div>
    </div>
  );
}
